class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  }

  handleDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const cartLink = event.target.closest('#cart-icon-bubble');
    if (cartLink) {
      event.preventDefault();
      this.open(cartLink);
      return;
    }

    const overlay = event.target.closest('#CartDrawer-Overlay');
    if (overlay && this.contains(overlay)) this.close();
  }

  handleDocumentKeydown(event) {
    if (!(event.target instanceof Element)) return;
    const cartLink = event.target.closest('#cart-icon-bubble');
    if (!cartLink || !['ENTER', 'SPACE'].includes(event.code.toUpperCase())) return;

    event.preventDefault();
    this.open(cartLink);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');
  }

  // The 'More to explore' thumbnails ship with data-src/data-srcset/data-sizes (see
  // snippets/cart-drawer.liquid) so a closed drawer never downloads them. Swap the real
  // attributes in when the drawer opens, and when its contents are re-rendered while open.
  hydrateRecommendationImages(root = this) {
    root.querySelectorAll('img.rgm-cart-recommendation__image[data-src], img.rgm-cart-recommendation__image[data-srcset]').forEach((img) => {
      if (img.dataset.sizes) img.setAttribute('sizes', img.dataset.sizes);
      if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
      if (img.dataset.src) img.setAttribute('src', img.dataset.src);
      img.removeAttribute('data-sizes');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    });
  }

  open(triggeredBy) {
    this.hydrateRecommendationImages();
    if (this.classList.contains('active')) {
      this.trapDrawerFocus();
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate');
    this.getBoundingClientRect();
    this.classList.add('active');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');

    let focusTrapped = false;
    const activateFocusTrap = () => {
      if (focusTrapped || !this.classList.contains('active')) return;
      focusTrapped = true;
      window.clearTimeout(this.focusTrapTimer);
      this.trapDrawerFocus();
    };
    this.addEventListener(
      'transitionend',
      activateFocusTrap,
      { once: true },
    );
    const focusDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 350;
    this.focusTrapTimer = window.setTimeout(activateFocusTrap, focusDelay);

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  trapDrawerFocus() {
    const containerToTrapFocusOn = this.querySelector('#CartDrawer');
    const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
    if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
  }

  close() {
    window.clearTimeout(this.focusTrapTimer);
    this.classList.remove('active');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasActive = this.classList.contains('active');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.setHeaderCartIconAccessibility();
      if (wasActive) {
        this.hydrateRecommendationImages();
        this.trapDrawerFocus();
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.handleAdd.bind(this));
      }

      // Dawn's cart JS (cart.js onCartUpdate and updateQuantity) also swaps the drawer body in.
      // A list that arrives while the drawer is open loads its images right away.
      connectedCallback() {
        const cartDrawer = this.closest('cart-drawer');
        if (
          cartDrawer &&
          cartDrawer.classList.contains('active') &&
          typeof cartDrawer.hydrateRecommendationImages === 'function'
        ) {
          cartDrawer.hydrateRecommendationImages(this);
        }
      }

      async handleAdd(event) {
        const button = event.target.closest('[data-cart-drawer-add]');
        if (!button || button.getAttribute('aria-disabled') === 'true') return;

        const cartDrawer = document.querySelector('cart-drawer');
        const error = this.querySelector('[data-cart-drawer-recommendations-error]');
        const addLabel = button.querySelector('[data-add-label]');
        const addingLabel = button.querySelector('[data-adding-label]');
        if (!cartDrawer || !button.dataset.variantId) return;

        button.setAttribute('aria-disabled', 'true');
        addLabel.hidden = true;
        addingLabel.hidden = false;
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', button.dataset.variantId);
          formData.append('quantity', '1');
          formData.append(
            'sections',
            cartDrawer.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          config.body = formData;

          const response = await fetch(`${routes.cart_add_url}`, config);
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorMessage);

          cartDrawer.setActiveElement(document.querySelector('#cart-icon-bubble'));
          cartDrawer.renderContents(result);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-recommendations',
            productVariantId: button.dataset.variantId,
            cartData: result,
          });
        } catch (exception) {
          if (error) {
            error.textContent = exception.message || this.dataset.errorMessage;
            error.hidden = false;
          }
          button.removeAttribute('aria-disabled');
          addLabel.hidden = false;
          addingLabel.hidden = true;
        }
      }
    }
  );
}
